import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Flex, Text, Wrapper, Button, OutlineButton, NakedButton, Input, ExternalLink, Icon } from 'roo-ui/components';
import Decimal from 'decimal.js';
import styled from '@emotion/styled';
import {
  getTotalPayableAfterVoucher,
  getTravelPassAmount,
  getTravelPassBin,
  getTravelPassEncryptedCard,
} from 'store/checkout/checkoutSelectors';
import { updatePayments } from 'store/checkout/checkoutActions';
import Currency from 'components/Currency';
import { PaymentMethodSummaryButton } from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/PaymentMethodSummary';
import { useDataLayer } from 'hooks/useDataLayer';
import useFormattedCashInput from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/hooks/useFormattedCashInput';
import { FieldLabel } from 'components/Form';
import TextButton from 'components/TextButton';
import { QCOM_TRAVELPASS_BALANCE_URL, QCOM_TRAVELPASS_TERMS_URL } from 'config';
import { getTravelPassErrors } from 'store/booking/bookingSelectors';
import BookingError from 'pages/Checkout/BookingError';
import AsyncUatpForm from './AsyncUatpForm';

const UnderlinedExternalLink = styled(ExternalLink)`
  text-decoration: underline;
`;

const STATES = {
  INITIAL: 'initial',
  ADDING: 'adding',
  EDITING: 'editing',
  EDITING_AMOUNT: 'editing_amount',
  ALLOCATED: 'allocated',
};

const maskedCardNumber = (travelPassBin) => `${String(travelPassBin).substr(0, 4)} ${String(travelPassBin).substr(4, 6)}XX XXXX XXX`;

const TravelPass = React.memo(({ ...rest }) => {
  const dispatch = useDispatch();
  const travelPassAmount = useSelector(getTravelPassAmount);
  const totalPayableAfterVoucher = useSelector(getTotalPayableAfterVoucher);
  const travelPassErrors = useSelector(getTravelPassErrors);
  const travelPassBin = useSelector(getTravelPassBin);
  const travelPassEncryptedCard = useSelector(getTravelPassEncryptedCard);
  const [travelPassState, setTravelPassState] = useState(travelPassAmount.greaterThan(0) ? STATES.ALLOCATED : STATES.INITIAL);
  const [bufferedTravelPassAmount, setBufferedTravelPassAmount] = useState(travelPassAmount);
  const [bufferedCardData, setBufferedCardData] = useState(travelPassEncryptedCard);
  const { emitInteractionEvent } = useDataLayer();

  const adjustUpdatedCashAmount = (amount) => (amount.greaterThan(totalPayableAfterVoucher) ? totalPayableAfterVoucher : amount);

  const { formattedCashAmount, handleCashInputChange, handleCashInputKeyUp, handleCashInputBlur } = useFormattedCashInput({
    cashAmount: travelPassAmount,
    updateCashAmount: setBufferedTravelPassAmount,
    adjustUpdatedCashAmount,
    showZeroAmounts: false,
  });

  const editTravelPass = useCallback(() => {
    setTravelPassState(STATES.EDITING);
    emitInteractionEvent({ type: 'TravelPass Link', value: 'Add Button Selected' });
  }, [emitInteractionEvent]);

  const editTravelPassAmount = useCallback(() => {
    setTravelPassState(STATES.EDITING_AMOUNT);
    emitInteractionEvent({ type: 'TravelPass Link', value: 'Change Button Selected' });
  }, [emitInteractionEvent]);

  const removeTravelPass = useCallback(() => {
    dispatch(updatePayments({ travelPass: { amount: new Decimal(0) } }));
    setTravelPassState(STATES.INITIAL);
    emitInteractionEvent({ type: 'Use TravelPass', value: 'Remove Button Selected' });
  }, [dispatch, emitInteractionEvent]);

  const cancelEdit = useCallback(() => {
    setTravelPassState(travelPassAmount.greaterThan(0) ? STATES.ALLOCATED : STATES.INITIAL);
    emitInteractionEvent({ type: 'Use TravelPass', value: 'Cancel Button Selected' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitInteractionEvent, travelPassAmount.toNumber()]);

  const applyTravelPass = useCallback(() => {
    setTravelPassState(STATES.ALLOCATED);
    dispatch(
      updatePayments({
        travelPass: {
          amount: bufferedTravelPassAmount,
          ...bufferedCardData,
        },
      }),
    );
    emitInteractionEvent({ type: 'Use TravelPass', value: 'Apply Button Selected' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bufferedTravelPassAmount.toNumber(), bufferedCardData, dispatch, emitInteractionEvent]);

  useEffect(() => {
    if (travelPassErrors.length) {
      setTravelPassState(STATES.EDITING_AMOUNT);
    }
  }, [dispatch, travelPassErrors.length]);

  const isValid =
    !!formattedCashAmount &&
    !!bufferedCardData?.encryptedCardNumber &&
    !!bufferedCardData?.encryptedExpiryMonth &&
    !!bufferedCardData?.encryptedExpiryYear;

  return (
    <Wrapper {...rest}>
      <Flex
        justifyContent="space-between"
        padding={4}
        borderColor="greys.alto"
        border={1}
        borderBottom={[STATES.EDITING, STATES.EDITING_AMOUNT].includes(travelPassState) ? 0 : 1}
        borderRadius={[STATES.EDITING, STATES.EDITING_AMOUNT].includes(travelPassState) ? 'exageratedRoundTopOnly' : 'exagerated'}
      >
        <Flex justifyContent="space-between" width="100%" alignItems="center">
          <Flex justifyContent="space-between" flexDirection={['column', 'row']} alignItems={['flex-start', 'center']} width="100%">
            <Text fontWeight="bold" fontSize={['sm', 'base']}>
              Qantas TravelPass
            </Text>
            <Box mr={[0, 4]}>
              {STATES.ALLOCATED && !travelPassAmount.isZero() && (
                <Currency amount={travelPassAmount} currency="AUD" fontSize="sm" data-testid="travel-pass-amount" />
              )}
            </Box>
          </Flex>
          {STATES.INITIAL === travelPassState && (
            <PaymentMethodSummaryButton onClick={editTravelPass} variant="primary" data-testid="edit-travel-pass-button">
              ADD
            </PaymentMethodSummaryButton>
          )}
          {STATES.ALLOCATED === travelPassState && (
            <PaymentMethodSummaryButton onClick={editTravelPassAmount} variant="primary" data-testid="edit-travel-pass-amount-button">
              CHANGE
            </PaymentMethodSummaryButton>
          )}
          {[STATES.EDITING, STATES.EDITING_AMOUNT].includes(travelPassState) && (
            <NakedButton onClick={cancelEdit} data-testid="cancel-edit-travel-pass-button">
              <Icon name="close" color="greys.steel" />
            </NakedButton>
          )}
        </Flex>
      </Flex>

      {[STATES.EDITING, STATES.EDITING_AMOUNT].includes(travelPassState) && (
        <Box padding={4} borderColor="greys.alto" border={1} borderRadius="exageratedRoundBottomOnly" data-testid="form-container">
          <BookingError errors={travelPassErrors} type="TravelPass" />
          <Box pb={4}>
            Choose the amount you would like to use from your Qantas TravelPass. We recommend you{' '}
            <UnderlinedExternalLink href={QCOM_TRAVELPASS_BALANCE_URL} color="brand.primary">
              check your balance <Icon name="openInNew" color="brand.primary" size={18} />.
            </UnderlinedExternalLink>
          </Box>
          <Flex flexDirection={['column', 'row']} mb={4}>
            <FieldLabel htmlFor="travel-pass-amount" mb={[4, 0]} pr={[0, 4]} width={['100%', '25%']}>
              Amount
              <Input
                id="travel-pass-amount"
                value={formattedCashAmount}
                onChange={handleCashInputChange}
                onKeyUp={handleCashInputKeyUp}
                onBlur={handleCashInputBlur}
                mb={0}
              />
            </FieldLabel>
            {travelPassState === STATES.EDITING && <AsyncUatpForm onChange={setBufferedCardData} />}
            {travelPassState === STATES.EDITING_AMOUNT && (
              <Flex flexDirection={['column', 'column', 'row']} width={['100%', '75%']} pb={4} pl={[0, 4]}>
                <Flex alignItems="flex-end" pb={[2, 0]}>
                  <Box mr={12}>
                    <Box>
                      <Text fontSize="base" fontWeight="bold" color="greys.steel">
                        Card number
                      </Text>
                    </Box>
                    <Flex height="52px" alignItems="center" data-testid="masked-card-number">
                      <Text fontSize="base" color="greys.alto">
                        {maskedCardNumber(travelPassBin)}
                      </Text>
                    </Flex>
                  </Box>
                  <Box mr={[0, 12]}>
                    <Box>
                      <Text fontSize="base" fontWeight="bold" color="greys.steel">
                        Expiry
                      </Text>
                    </Box>
                    <Flex height="52px" alignItems="center">
                      <Text fontSize="base" color="greys.alto">
                        XX/XX
                      </Text>
                    </Flex>
                  </Box>
                </Flex>
              </Flex>
            )}
          </Flex>
          <Flex flexDirection={['column-reverse', 'row']} justifyContent="space-between" mb={4}>
            <Flex flexDirection={['column', 'row']} mt={4}>
              <Button
                onClick={applyTravelPass}
                disabled={!isValid}
                variant="primary"
                width={['100%', 'auto']}
                data-testid="apply-travel-pass-button"
                mb={[4, 0]}
                mr={[0, 8]}
              >
                APPLY
              </Button>

              <OutlineButton onClick={cancelEdit} variant="primary" width={['100%', 'auto']} data-testid="cancel-edit-button" mb={[4, 0]}>
                CANCEL
              </OutlineButton>
            </Flex>
            {travelPassState === STATES.EDITING_AMOUNT && (
              <Flex alignItems="center" mb={[4, 0]}>
                <TextButton onClick={editTravelPass} mr={8} data-testid="change-travel-pass-details-button">
                  <Icon name="modeEdit" />
                  Change card details
                </TextButton>
                <TextButton onClick={removeTravelPass} data-testid="remove-travel-pass-button">
                  <Icon name="deleteIcon" />
                  Remove
                </TextButton>
              </Flex>
            )}
          </Flex>
          <UnderlinedExternalLink href={QCOM_TRAVELPASS_TERMS_URL}>
            Terms and conditions apply <Icon name="openInNew" size={18} />{' '}
          </UnderlinedExternalLink>
        </Box>
      )}
    </Wrapper>
  );
});

TravelPass.displayName = 'TravelPass';

export default TravelPass;
